import React from "react";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CommunityFilterSelection } from "../../utils/communityAggregation";
import SegmentedControl from "./SegmentedControl";
import { CountUnit, SelectOption } from "./types";

interface CommunityFilterBarProps {
	regionOptions: SelectOption[];
	countryOptions: SelectOption[];
	categoryOptions: SelectOption[];
	expertiseOptions: SelectOption[];
	sectorOptions: SelectOption[];
	filters: CommunityFilterSelection;
	onChange: (patch: Partial<CommunityFilterSelection>) => void;
	unit: CountUnit;
	onUnitChange: (unit: CountUnit) => void;
	selectionLabel: string;
}

const selectSx = { minWidth: 200 };

const CommunityFilterBar: React.FC<CommunityFilterBarProps> = ({
	regionOptions,
	countryOptions,
	categoryOptions,
	expertiseOptions,
	sectorOptions,
	filters,
	onChange,
	unit,
	onUnitChange,
	selectionLabel
}) => {
	const { t } = useTranslation();

	const renderSelect = (
		label: string,
		value: string,
		options: SelectOption[],
		onValue: (value: string) => void
	) => (
		<TextField
			select
			size="small"
			label={label}
			value={value}
			onChange={event => onValue(event.target.value)}
			sx={selectSx}
		>
			{options.map(option => (
				<MenuItem key={option.value} value={option.value}>
					{option.label}
				</MenuItem>
			))}
		</TextField>
	);

	return (
		<Box display="flex" alignItems="flex-end" gap={2} flexWrap="wrap">
			{renderSelect(t("communityDashboard.filters.region"), filters.region, regionOptions, value =>
				onChange({ region: value })
			)}
			{renderSelect(
				t("communityDashboard.filters.country"),
				filters.country,
				countryOptions,
				value => onChange({ country: value })
			)}
			<Box>
				<Typography
					variant="caption"
					color="text.secondary"
					fontWeight={700}
					display="block"
					mb={0.5}
				>
					{t("communityDashboard.filters.profileType")}
				</Typography>
				<SegmentedControl
					ariaLabel={t("communityDashboard.filters.profileType")}
					options={categoryOptions}
					value={filters.category}
					onChange={value => onChange({ category: value })}
				/>
			</Box>
			{renderSelect(
				t("communityDashboard.filters.expertise"),
				filters.expertise,
				expertiseOptions,
				value => onChange({ expertise: value })
			)}
			{renderSelect(t("communityDashboard.filters.sector"), filters.sector, sectorOptions, value =>
				onChange({ sector: value })
			)}
			<Box>
				<Typography
					variant="caption"
					color="text.secondary"
					fontWeight={700}
					display="block"
					mb={0.5}
				>
					{t("communityDashboard.filters.display")}
				</Typography>
				<SegmentedControl
					ariaLabel={t("communityDashboard.filters.display")}
					options={[
						{ value: "values", label: t("communityDashboard.filters.values") },
						{ value: "percent", label: t("communityDashboard.filters.percent") }
					]}
					value={unit}
					onChange={onUnitChange}
				/>
			</Box>
			<Box flex={1} />
			<Typography variant="caption" color="text.secondary" pb={0.5} whiteSpace="nowrap">
				{selectionLabel}
			</Typography>
		</Box>
	);
};

export default CommunityFilterBar;
